import { computed, ref } from '@nuxtjs/composition-api';
import useTlmCart from '~/composable/useTlmCart';
import useErrorHandler from '~/composable/useErrorHandler';
import { WARNING_CODE } from '@/constants/ErrorCodes'
import useCartQueries from '~/composable/useCartQueries';
import { CHECKOUT_FLOW } from '~/constants/CheckoutFlow'
import useProductConfigurationCatalog from '~/composable/useProductConfigurationCatalog'
import { getUniqueCodes, mapCodesToAddedPlayAddOnServices } from '~/utils/playAddOnServices'

const nonEmptyOrNull = arr => (Array.isArray(arr) && arr.length > 0) ? arr : null

export default function useSubscriptions (appContext) {
  const validationResult = ref(null);
  const preValidationResult = ref(null);
  const hubbleClient = appContext.$hubble;
  const { catchApiFailureWithNoAction, stopFlow } = useErrorHandler()
  if (!appContext || !hubbleClient) {
    throw new Error('You must provide the nuxt app context to use this composable');
  }
  const { subscriptionList, installments, getCartID, mixPoList, partnerAddonsList, cart } = useTlmCart(appContext)
  const { cartOnlyContainsSubscription } = useCartQueries(appContext, cart)
  const { loadProductConfigurationCatalog } = useProductConfigurationCatalog(appContext)

  const getAddedPlayAddOnServices = async (mixCodes = mixPoList.value, addonCodes = partnerAddonsList.value) => {
    const codes = getUniqueCodes(mixCodes)
    if (!codes.length && !getUniqueCodes(addonCodes).length) {
      return []
    }
    const catalog = await loadProductConfigurationCatalog()
    return mapCodesToAddedPlayAddOnServices(codes, catalog, addonCodes)
  }

  const normalizeSubscriptionSignUpData = async (subscriptionSignUpData = {}) => {
    const normalizedPayload = { ...subscriptionSignUpData }

    const checkoutFlow = normalizedPayload.checkoutFlow

    // Device + subscription flow: backend expects raw mixServices (array of PlayPoList strings),
    // and does NOT want addedPlayAddOnServices mapping.
    if (checkoutFlow === CHECKOUT_FLOW.DEVICE_WITH_SUBSCRIPTION) {
      normalizedPayload.mixServices = nonEmptyOrNull(
        Array.isArray(normalizedPayload.mixServices) ? normalizedPayload.mixServices : []
      )
      delete normalizedPayload.addedPlayAddOnServices
    } else {
      // Subscription-only flows: backend expects mapped addedPlayAddOnServices.
      if (Array.isArray(normalizedPayload.mixServices)) {
        normalizedPayload.addedPlayAddOnServices = nonEmptyOrNull(await getAddedPlayAddOnServices(normalizedPayload.mixServices))
      } else if (!Array.isArray(normalizedPayload.addedPlayAddOnServices)) {
        normalizedPayload.addedPlayAddOnServices = nonEmptyOrNull(await getAddedPlayAddOnServices())
      }

      delete normalizedPayload.mixServices
    }

    delete normalizedPayload.checkoutFlow
    return normalizedPayload
  }

  const getSubscriptionSignUpData = () => {
    return normalizeSubscriptionSignUpData({
      isSimOnly: cartOnlyContainsSubscription.value,
      bundleId: subscriptionList?.value?.sku,
      isCompanyCustomer: true,
      shopReservationDTO: cartOnlyContainsSubscription.value
        ? null
        : {
            installments: installments?.value
          }
    })
  }
  const validateSubscriptions = async (subscriptionSignUpDataNew) => {
    const payload = await normalizeSubscriptionSignUpData(subscriptionSignUpDataNew)
    const response = await hubbleClient.subscriptions.validateNewSubscription(payload, getCartID.value).catch(err => stopFlow(err))
    validationResult.value = response?.data
    return validationResult.value
  };
  const preValidateSubscriptions = async () => {
    const subscriptionSignUpData = await getSubscriptionSignUpData()
    const response = await hubbleClient.subscriptions.preValidateNewSubscription(subscriptionSignUpData).catch(err => catchApiFailureWithNoAction(err))
    preValidationResult.value = response?.data
    return preValidationResult.value
  };
  const genProperAccessToken = (accessToken, accessTokenType, userName = '', password = '') => {
    if (accessTokenType === 'UN_PW') {
      return {
        userName,
        password,
        accessTokenType
      }
    } else {
      return {
        accessToken,
        accessTokenType
      }
    }
  }
  const getPayload = async ({ accessToken, accessTokenType, phoneNumber, esimOnlyDevice, bundleID, cartId, mixServices, userName = '', password = '' }) => {
    const resolvedMixServices = Array.isArray(mixServices) ? mixServices : (mixServices ?? mixPoList.value ?? [])
    return {
      accessToken: genProperAccessToken(accessToken, accessTokenType, userName, password),
      bundleId: bundleID,
      numberInfo: {
        mobileNumber: phoneNumber,
        esimOnlyDevice
      },
      cartId,
      // For change-subscription validations we also include raw mixServices (PlayPoList) when available.
      // Some backend paths expect this as the source of truth.
      mixServices: nonEmptyOrNull(resolvedMixServices),
      addedPlayAddOnServices: nonEmptyOrNull(await getAddedPlayAddOnServices(mixServices))
    }
  }


  const preValidateChangeSubscription = async ({ accessToken, accessTokenType, phoneNumber, bundleID, cartID, mixServices, userName, password }) => {
    if (accessTokenType !== 'MIT_ID' && accessTokenType !== 'JWT' && accessTokenType !== 'UN_PW') {
      throw new Error('Only AccessTokenTypes of JWT and MIT_ID are allowed')
    }
    const payload = await getPayload({ accessToken, accessTokenType, phoneNumber, bundleID, cartId: cartID, mixServices, userName, password })
    const response = await hubbleClient.subscriptions.preValidateChangeSubscription(payload).catch(err => catchApiFailureWithNoAction(err))
    preValidationResult.value = response?.data
    return preValidationResult.value
  };
  const validateChangeSubscription = async ({ accessToken, accessTokenType, phoneNumber, esimOnlyDevice, bundleID, cartID, mixServices, userName, password }) => {
    if (accessTokenType !== 'MIT_ID' && accessTokenType !== 'JWT' && accessTokenType !== 'UN_PW') {
      throw new Error('Only AccessTokenTypes of JWT and MIT_ID are allowed')
    }
    const payload = await getPayload({ accessToken, accessTokenType, phoneNumber, esimOnlyDevice, bundleID, cartId: cartID, mixServices, userName, password })
    const response = await hubbleClient.subscriptions.validateChangeSubscription(payload).catch(err => catchApiFailureWithNoAction(err))
    preValidationResult.value = response?.data
    return preValidationResult.value
  };
  const isSubscriptionUpgrade = computed(() => {
    const isDowngrade = preValidationResult.value?.hasWarnings &&
      preValidationResult.value.warnings.find(warning => WARNING_CODE.CHANGE_END_OF_MONTH.includes(warning.notificationCode)) !== undefined
    // BE doesnt send the Upgrade message, so assumption at FE is that if its not a downgrade its an upgrade
    // const isUpgrade = preValidationResult.value.hasWarnings &&
    //   preValidationResult.value.warnings.find(warning => WARNING_CODE.CHANGE_END_OF_DAY === warning.notificationCode) !== undefined
    return !isDowngrade
  })
  return {
    isSubscriptionUpgrade,
    preValidationResult,
    validationResult,
    validateSubscriptions,
    preValidateSubscriptions,
    validateChangeSubscription,
    preValidateChangeSubscription
  }
}
